import { useEffect, useState } from "react";
import { Badge, Card, Container } from "@thema/ui";
import { HeroLayers } from "../components/HeroLayers";
import { PrincipleCard } from "../components/PrincipleCard";
import { useParallax } from "../hooks/useParallax";
import { TDDListSidebar } from "../components/tdd/TDDListSidebar";
import { TDDDocumentPanel } from "../components/tdd/TDDDocumentPanel";
import { tddList } from "../data/tdd/tddList";
import { tddDocs } from "../data/tdd/tddDocs";
import { ContactModal } from "../components/ContactModal";
import { UpdateStrip } from "../components/UpdateStrip";
// Static asset handled by Vite; type is declared in vite-env.d.ts
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ThemaLogo from "../../Logo.svg";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import SingleLogo from "../../single-logo.svg";

const principles = [
  {
    title: "Designed for Real Conditions",
    body:
      "Systems behave consistently on-chain, with explicit lifecycle rules and rational responses to uncertainty.",
    tag: "EXECUTION LAYER • DETERMINISTIC OPS",
    docId: "DOC 01",
  },
  {
    title: "Explicit Mechanics",
    body:
      "Each instrument follows defined rules for shaping exposure and attributing PnL. Structure makes our behavior legible.",
    tag: "STRUCTURED MECHANICS • TRANSPARENT PNL",
    docId: "DOC 02",
  },
  {
    title: "Validated Through Simulation",
    body:
      "Designs are modeled and backtested against historical data with deterministic replay. What doesn't validate doesn't move forward.",
    tag: "HISTORICAL REPLAY • AUDIT-GRADE METRICS",
    docId: "DOC 03",
  },
];


function getCurrentTime() {
  const now = new Date();
  const hours = String(now.getUTCHours()).padStart(2, "0");
  const minutes = String(now.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export default function Home() {
  const [activeTdd, setActiveTdd] = useState("TDD-5");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(getCurrentTime());
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);

  useEffect(() => {
    // Detect mobile and desktop
    const checkBreakpoints = () => {
      setIsMobile(window.innerWidth < 768 || "ontouchstart" in window);
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkBreakpoints();
    window.addEventListener("resize", checkBreakpoints);
    return () => window.removeEventListener("resize", checkBreakpoints);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleScroll = () => {
      const progress = Math.min(window.scrollY / (window.innerHeight || 1), 1);
      setScrollProgress(Number(progress.toFixed(3)));
      
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getCurrentTime());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Headline micro-scroll parallax: 2-4px total, trigger 0→200px scroll (disabled on mobile/tablet)
  const headlineParallax = useParallax(isDesktop ? 0.02 : 0);
  
  // Subheading
  const definitionText = "Thema is a protocol for building market-aware crypto assets directly on decentralized exchanges.";

  const activeTddItem = tddList.find((tdd) => tdd.id === activeTdd);
  const activeTddIndex = tddList.findIndex((tdd) => tdd.id === activeTdd);
  const activeDocument = tddDocs[activeTdd];

  const scrollToTDD = () => {
    const tddSection = document.getElementById("tdd-viewer");
    if (tddSection) {
      tddSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <main className="flex h-auto min-h-0 flex-col bg-[#0A0F16] text-thema-text overflow-x-hidden overflow-y-visible w-full max-w-[100vw] lg:min-h-screen">
      {/* News Strip at Top */}
      <UpdateStrip />
      
      {/* Mobile & Tablet Header */}
      <header className="lg:hidden sticky top-0 z-50 w-full px-4 py-3 backdrop-blur bg-[#0A0F16]/90 border-b border-thema-border-hairline overflow-visible">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={ThemaLogo}
              className="h-16 w-auto"
              alt="Thema"
            />
            <span className="text-xs tracking-widest text-green-100 font-mono">
              ONLINE
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <button 
              onClick={scrollToTDD} 
              className="text-white/80 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
            >
              Docs
            </button>
            <button 
              onClick={() => setIsContactOpen(true)} 
              className="text-white/80 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
            >
              Contact
            </button>
          </div>
        </div>
      </header>

      {/* Left Margin OS Spine - Design.md: dynamic metadata by scroll zone */}
      <div className="hidden lg:flex fixed left-0 top-0 z-50 h-full w-[132px] border-r border-thema-border-hairline bg-[#0A0F16]/80 backdrop-blur-sm">
        {/* Neon Accent Line - Design.md: rgba(77,183,247,0.18), opacity 5-7% */}
        <div className="absolute left-0 top-0 h-full w-px bg-[rgba(77,183,247,0.18)] opacity-[0.06]" />
        
        <div className="relative flex h-full flex-col items-start justify-between px-4 py-8">
          <div className="space-y-6">
            <div className="flex items-center">
              {/* System identity mark - symbol only, non-interactive */}
              <div className="thema-logo-symbol-wrapper" aria-hidden="true">
                <img src={ThemaLogo} alt="" className="thema-logo" />
              </div>
              
            </div>
            <div className="h-px w-12 bg-gradient-to-r from-thema-royal-blue/30 to-transparent" />
            
            {/* Build Metadata - Static identity only */}
            <div className="space-y-2 font-mono text-[9px] uppercase tracking-[0.15em] text-thema-micro opacity-60">
              <div>SYSTEM MODE: <span className="text-green-100">ONLINE</span></div>
              <div>BUILD: <span className="build-hash">945de72</span></div>
            </div>
          </div>
          
          {/* Horizontal Dividers - Design.md: every 320px */}
          <div className="absolute left-0 top-[320px] h-px w-full bg-thema-border-hairline opacity-30" />
          <div className="absolute left-0 top-[640px] h-px w-full bg-thema-border-hairline opacity-30" />
          <div className="absolute left-0 top-[960px] h-px w-full bg-thema-border-hairline opacity-30" />
          
          {/* Dynamic Metadata by Scroll Zone - Design.md spec */}
          <div className="space-y-4 font-mono text-[12px] uppercase tracking-[0.14em] text-white/35">
            {scrollProgress < 0.3 ? (
              <>
                <div>PAGE 001</div>
                <div className="cursor-help hover:text-thema-royal-blue text-green-400" onClick={() => {
                    window.location.href = "mailto:aamir@thema.financial";
                  }}>Contact Thema</div>
              </>
            ) : scrollProgress < 0.6 ? (
              <>
                <div>PAGE 002</div>
                <div>DOCSET: PRINCIPLES</div>
              </>
            ) : scrollProgress < 0.9 ? (
              <>
                <div>PAGE 003</div>
                <div>DOCSET: TECHNICAL SPEC</div>
              </>
            ) : (
              <>
                <div>PAGE 004</div>
                <div>SYSTEM EXIT • GOODBYE</div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="ml-0 w-full max-w-none pt-16 lg:ml-[132px] lg:max-w-[calc(100vw-132px)] lg:pt-0">
        {/* Hero Section */}
        <section className="relative isolate w-full h-auto min-h-0 overflow-visible bg-[#0A0F16] text-thema-text max-w-none">
          {/* Blur lines with low opacity */}
          <div className="hero-blur-lines pointer-events-none absolute inset-0 w-full max-w-full overflow-hidden" />
          
          {/* Ambient glow behind hero cards */}
          <div className="hero-ambient-glow pointer-events-none absolute inset-0 w-full max-w-full overflow-hidden" />
          
          {/* Grid background with noise */}
          <div className="hero-grid pointer-events-none absolute inset-0 w-full max-w-full overflow-hidden" />
          <div className="hero-circuit pointer-events-none absolute inset-0 w-full max-w-full overflow-hidden transform-none lg:transform" />
          <div className="hero-scanlines pointer-events-none absolute inset-0 w-full max-w-full overflow-hidden" />

          {/* Optional hero watermark - symbol only, extremely subtle */}
          <div className="hero-watermark pointer-events-none absolute inset-0 w-full max-w-full overflow-hidden" aria-hidden="true">
            <div className="hero-watermark-symbol">
              <img src={ThemaLogo} alt="" className="hero-watermark-image" />
            </div>
          </div>
          
          {/* Hero Layers with parallax and hover */}
          <div className="absolute inset-0 pointer-events-none w-full max-w-full overflow-hidden">
            <HeroLayers isMobile={isMobile} />
          </div>

          <Container className="relative z-10 flex h-auto min-h-0 flex-col justify-center py-12 sm:py-16 md:py-20 lg:py-28 w-full max-w-none md:max-w-screen-xl lg:min-h-screen">
            <div className="max-w-none md:max-w-2xl space-y-8 w-full">
              {/* Metadata Row - Live/session state only */}
              <div className="hero-meta-row space-y-1">
                <p className="hero-meta font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
                  <span className="system-status version">v0.1.0-beta</span> •{" "}
                  <span className="system-status live">THEMA ENGINE ONLINE</span>
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
                  REV 04A • INTERNAL • PRIVATE BETA SIGNUP
                </p>
              </div>

              {/* Enhanced Headline - Loads immediately with floating shadow */}
              <h1 
                className="hero-headline-immediate font-serif text-[36px] sm:text-[42px] md:text-[56px] lg:text-[80px] font-semibold leading-[0.95] tracking-[0.8px] text-thema-text mb-[-12px] max-w-[20ch] lg:max-w-none break-words transform-none lg:transform"
                style={{ transform: isDesktop ? `translateY(${-headlineParallax}px)` : 'none' }}
              >
                <span className="bg-gradient-to-b from-thema-text via-thema-text to-thema-text-soft bg-clip-text text-transparent drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)] shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
                  Structured Instruments. Native to DEXs.
                </span>
                <span className="hero-underline block h-[4px] w-0 mt-3 bg-thema-royal-blue/60 backdrop-blur-sm shadow-[0_0_16px_rgba(77,183,247,0.4)] transition-all duration-1000 ease-thema-smooth blur-[2px]" />
              </h1>
              
              {/* Additional blurred lines in hero */}
              <div className="hero-additional-lines">
                <div className="hero-line-1" />
                <div className="hero-line-2" />
                <div className="hero-line-3" />
              </div>

              {/* Subheading */}
              <div>
                {/* Subheadline - No typing animation, lighter tone, decreased opacity */}
                <p className="hero-subheadline text-base sm:text-lg md:text-xl leading-relaxed sm:leading-relaxed md:leading-relaxed text-thema-text-soft/70 max-w-none md:max-w-[600px] opacity-0 animate-[textReveal_0.5s_var(--thema-smooth)_0.3s_forwards] mb-3 md:mb-0 w-full">
                  {definitionText}
                </p>
              </div>

              

              

              

              

              {/* Micro-annotations - Decreased opacity */}
              <div className="flex flex-wrap gap-6 font-mono text-[10px] uppercase tracking-[0.15em] text-thema-micro opacity-30 pt-2">
                <span>DOC 01 • REV 0.4</span>
                <span>MODEL: EXECUTION</span>
                <span>REF ID: /tdd/001</span>
              </div>
            </div>
          </Container>
        </section>

        {/* Principles Section */}
        <section className="relative border-t border-thema-border-hairline bg-[#0A0F16] py-12 sm:py-16 md:py-20 overflow-x-hidden overflow-y-visible w-full max-w-none">
          {/* Background transition fade overlay */}
          <div className="absolute inset-x-0 top-0 h-[120px] bg-gradient-to-b from-black/0 to-black/30 pointer-events-none w-full max-w-full" />
          
          {/* Blur lines for other sections */}
          <div className="section-blur-lines pointer-events-none absolute inset-0 w-full max-w-full overflow-hidden" />
          
          <Container className="w-full max-w-none md:max-w-screen-xl">
            <div className="mb-16 max-w-none md:max-w-[720px] w-full">
              {/* Technical label */}
              <p className="text-[10px] text-white/35 tracking-[0.18em] uppercase mb-3 font-mono">
                Principles
              </p>
              
              {/* Heading with improved styling */}
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] max-w-full">
                Our Approach
              </h2>
              
              {/* Thin underline */}
              <div className="h-[1px] bg-white/10 w-[120px] mt-2" />
            </div>
            
            {/* Cards grid with improved spacing - stack vertically on mobile, equal heights */}
            <div className="grid gap-x-6 gap-y-6 grid-cols-1 md:grid-cols-3 items-stretch">
              {principles.map((principle) => (
                <PrincipleCard
                  key={principle.title}
                  tag={principle.tag}
                  title={principle.title}
                  body={principle.body}
                  docId={principle.docId}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </Container>
        </section>

        {/* TDD Viewer Section */}
        <section id="tdd-viewer" data-section="tdd-viewer" className="border-t border-thema-border-hairline bg-[#0A0F16] py-12 sm:py-16 md:py-20 overflow-x-hidden overflow-y-visible w-full max-w-none">
          <Container className="w-full max-w-none md:max-w-screen-xl">
            <header className="max-w-none md:max-w-2xl space-y-4 mb-12 w-full">
              <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-thema-teal opacity-75">
                THEMA DESCRIPTION DOCUMENTS
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold tracking-[0.5px] text-thema-text max-w-full">
                Under The Hood
              </h2>
              <p className="text-thema-text-soft opacity-85 leading-relaxed sm:leading-relaxed">
              Thema combines multiple technologies to produce a coherent and capable system. The 5 systems our private beta surfaces are described below:
              </p>
            </header>

            {/* Two-column layout: Left sidebar (280px) + Right document panel (flexible) */}
            {/* Responsive: dropdown selector on mobile, side-by-side on desktop */}
            
            {/* Mobile TDD Selector */}
            <div className="lg:hidden mb-6">
              <label htmlFor="tdd-selector" className="block font-mono text-[10px] uppercase tracking-[0.15em] text-thema-micro opacity-60 mb-2">
                SELECT TDD
              </label>
              <select
                id="tdd-selector"
                value={activeTdd}
                onChange={(e) => setActiveTdd(e.target.value)}
                className="w-full bg-[#0F141A] border border-thema-border-hairline rounded-sm px-4 py-3 font-mono text-sm text-white focus:outline-none focus:border-thema-royal-blue/50 focus:ring-1 focus:ring-thema-royal-blue/30"
              >
                {tddList.map((tdd) => (
                  <option key={tdd.id} value={tdd.id}>
                    {tdd.id} • {tdd.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-start">
              {/* Left Sidebar - TDD Navigation (hidden on mobile) */}
              <div className="hidden lg:block w-full lg:w-[280px] flex-shrink-0">
                <TDDListSidebar
                  tddList={tddList}
                  activeTdd={activeTdd}
                  onSelectTdd={setActiveTdd}
                />
              </div>

              {/* Right Panel - Document Viewer */}
              {activeTddItem && activeDocument && (
                <TDDDocumentPanel
                  tddId={activeTdd}
                  title={activeTddItem.title}
                  document={activeDocument}
                  currentIndex={activeTddIndex}
                  onOpenContact={() => setIsContactOpen(true)}
                />
              )}
            </div>
          </Container>
        </section>

        {/* Optional: Horizontal Status Ticker - Design.md Tier 2 */}
        <div className="border-t border-thema-border-hairline bg-[#0A0F16] py-3 overflow-x-hidden overflow-y-visible w-full max-w-none">
          <div className="status-ticker-wrapper">
            <div className="status-ticker whitespace-nowrap">
              {(() => {
                const tickerText = "RISK POLICY LOADING • EARLY AUDITS CONDUCTED • v0.1.0-BETA";
                const duplicatedText = [tickerText, tickerText];
                return duplicatedText.map((text, index) => (
                  <span key={index} className="inline-block font-mono text-[10px] uppercase tracking-[0.15em] text-thema-micro opacity-45">
                    {text}
                    {index < duplicatedText.length - 1 && <span className="px-4">•</span>}
                  </span>
                ));
              })()}
            </div>
          </div>
        </div>

        {/* Footer - Design.md: border-top 1px solid rgba(255,255,255,0.05) */}
        <footer className="border-t border-[rgba(255,255,255,0.05)] bg-[#0A0F16] py-10 w-full max-w-none overflow-x-hidden overflow-y-visible">
          <Container className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between w-full max-w-none md:max-w-screen-xl">
            <div className="space-y-1 font-mono text-[11px] uppercase tracking-[0.14em] text-thema-micro opacity-55">
              <div>THEMA INFRASTRUCTURE</div>
              <div>VERSION: 0.1.0-beta</div>
              <div>COPYRIGHT © 2025</div>
            </div>
            <div className="flex gap-6">
              <a
                href="https://github.com/thema-financial/thema-zero"
                target="_blank"
                rel="noreferrer"
                className="text-thema-text-soft text-[11px] transition hover:text-thema-royal-blue hover:underline hover:underline-offset-2"
              >
                GitHub
              </a>
              <a
                href="https://x.com/themafinancial"
                target="_blank"
                rel="noreferrer"
                className="text-thema-text-soft text-[11px] transition hover:text-thema-teal hover:underline hover:underline-offset-2"
              >
                Socials
              </a>
            </div>
          </Container>
          
          {/* Soft Divider */}
          <div className="mt-6 border-t border-thema-border-hairline/30 pt-4" />
          
          {/* Legal Disclaimer */}
          <Container className="w-full max-w-none md:max-w-screen-xl">
            <div className="max-w-5xl space-y-3">
              <p className="text-[10px] leading-relaxed font-mono text-thema-text-soft/45 italic">
                *Thema may run limited strategies with proprietary capital for validation based on v0.1-beta: an early-stage protocol design and tooling. Content reflects architectural intent, not operational commitments.
              </p>

              <p className="text-[10px] leading-relaxed font-mono text-thema-text-soft/45 italic">
                *Third-party tooling access, if any, is experimental and provided only through contractually licensed operators subject to applicable regulations. Thema makes no claims regarding returns, risk, or market outcomes. Not an offer, advisory service, or solicitation.
              </p>
            </div>
          </Container>
        </footer>
      </div>

      {/* Floating Contact Logo Button - Responsive */}
      <div 
        className={`fixed bottom-5 right-5 z-50 transition-opacity duration-300 ${
          // Mobile: always visible
          // Desktop/Tablet: visible after Page 1 scroll (scrollProgress >= 0.3)
          isMobile || scrollProgress >= 0.3 
            ? "opacity-100 pointer-events-auto" 
            : "opacity-0 pointer-events-none"
        }`}
      >
        <button
          onClick={() => {
                    window.location.href = "mailto:aamir@thema.financial";
                  }}
          className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-[#0A0F16]/90 border border-thema-border-hairline/60 shadow-[0_0_30px_rgba(0,0,0,0.5)] backdrop-blur-sm flex items-center justify-center transition-all duration-200 hover:shadow-[0_0_40px_rgba(77,183,247,0.4)] hover:border-thema-royal-blue/50 group"
          aria-label="Contact Thema"
        >
          <img 
            src={SingleLogo} 
            alt="Thema Logo" 
            className="h-20 w-20 md:h-20 md:w-20 object-contain transition-transform duration-200 group-hover:scale-110"
          />
        </button>
      </div>

      <ContactModal open={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </main>
  );
}
